/**
 * DomainRequestModal — used in both client (BuyDomain) and partner (Domains) portals
 *
 * Props:
 *   open        — boolean
 *   onClose     — () => void
 *   domain      — string   (e.g. "example.com")
 *   years       — number
 *   price       — number
 *   isEmailOnly — boolean  (true = standalone email hosting request for existing domain)
 *   onSubmit    — async (payload) => void
 *                 payload = {domain, years, email_hosting_requested, email_accounts_count,
 *                            email_usernames, notes, domain_lock, whois_privacy}
 *   submitting  — boolean
 */
import { useState, useEffect } from 'react';
import { X, Mail, Globe, Lock, ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ACCOUNT_COUNTS = [2, 3, 5, 7, 10];

const DEFAULT_SETTINGS = {
  email_price_per_account: 360,
  email_original_price: 900,
  whois_privacy_price: 299,
  domain_protector_price: 199,
  email_hosting_features: [],
};

export function DomainRequestModal({
  open,
  onClose,
  domain = '',
  years = 1,
  price = 0,
  isEmailOnly = false,
  onSubmit,
  submitting = false,
  forClientId = '',
  forClientName = '',
  overrideSettings = null,   // when provided, used instead of fetching domain-settings/effective
}) {
  const [accountCount, setAccountCount]   = useState(2);
  const [usernames, setUsernames]         = useState([]);
  const [notes, setNotes]                 = useState('');
  const [domainProtector, setDomainProtector] = useState(false);
  const [whoisPrivacy, setWhoisPrivacy]   = useState(true);
  const [sslRequired, setSslRequired]     = useState(true);
  const [settings, setSettings]           = useState(DEFAULT_SETTINGS);
  const [usernameErrors, setUsernameErrors] = useState([]);

  // Fetch pricing settings — use effective endpoint (auth-aware)
  useEffect(() => {
    if (!open) return;
    if (overrideSettings) {
      // Partner buying for a client — use partner's own markup pricing
      setSettings(s => ({ ...s, ...overrideSettings }));
    } else {
      // Fetch role-aware settings:
      // - Partners (SP/CP): admin base price (their cost)
      // - Clients: parent's markup price
      axios.get(`${API}/domain-settings/effective`)
        .then(r => setSettings(r.data))
        .catch(() => {
          axios.get(`${API}/domain-settings/public`)
            .then(r => setSettings(r.data))
            .catch(() => setSettings(DEFAULT_SETTINGS));
        });
    }
  }, [open, overrideSettings]);

  // Sync username fields count to accountCount
  useEffect(() => {
    setUsernames(Array.from({ length: accountCount }, (_, i) => ({
      id: i,
      value: '',
    })));
    setUsernameErrors([]);
  }, [accountCount]);

  // Reset on modal open
  useEffect(() => {
    if (open) {
      setAccountCount(2);
      setNotes('');
      setDomainProtector(false);
      setWhoisPrivacy(true);
      setSslRequired(true);
      setUsernameErrors([]);
    }
  }, [open]);

  if (!open) return null;

  const updateUsername = (id, val) => {
    setUsernames(u => u.map(x => x.id === id ? { ...x, value: val.toLowerCase().replace(/[^a-z0-9._+-]/g, '') } : x));
    setUsernameErrors(e => e.filter(i => i !== id));
  };

  const validate = () => {
    const errors = usernames
      .filter(u => !u.value.trim())
      .map(u => u.id);
    setUsernameErrors(errors);
    return errors.length === 0;
  };

  // Pricing calculations — email is yearly, tied to domain registration period
  const emailCostPerYear = settings.email_price_per_account * accountCount;     // for 1 year
  const emailCostTotal   = emailCostPerYear * years;                             // total for registration period
  const emailOriginalPerYear = settings.email_original_price * accountCount;
  const emailOriginalTotal   = emailOriginalPerYear * years;
  const whoisCost        = whoisPrivacy ? settings.whois_privacy_price * years : 0;
  const domainProtectorCost = domainProtector ? settings.domain_protector_price * years : 0;
  const domainCost       = isEmailOnly ? 0 : price;
  // PRE-GST subtotal
  const subtotalPreGST   = domainCost + emailCostTotal + whoisCost + domainProtectorCost;
  // 18% GST added ON TOP
  const GST_RATE         = 18;
  const gstAmount        = Math.round(subtotalPreGST * GST_RATE) / 100;
  const totalDue         = Math.round((subtotalPreGST + gstAmount) * 100) / 100;  // Grand total (incl. GST)

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error(`Please fill in all ${accountCount} email usernames before submitting.`);
      return;
    }
    const payload = {
      domain,
      years,
      email_hosting_requested: true,
      email_only: isEmailOnly,
      domain_protector: domainProtector,
      whois_privacy: whoisPrivacy,
      ssl_required: sslRequired,
      email_accounts_count: accountCount,
      email_usernames: usernames.map(u => u.value.trim()).filter(Boolean),
      // Exact amounts shown in the Order Summary — stored for display in order lists
      quoted_domain_price:    domainCost,
      quoted_email_cost:      emailCostTotal,
      quoted_whois_cost:      whoisCost,
      quoted_protector_cost:  domainProtectorCost,
      quoted_subtotal:        subtotalPreGST,
      quoted_gst:             gstAmount,
      quoted_total:           totalDue,
    };
    if (notes.trim()) payload.notes = notes.trim();
    if (forClientId) payload.for_client_id = forClientId;
    await onSubmit(payload);
  };

  const filledCount = usernames.filter(u => u.value.trim()).length;
  const allFilled   = filledCount === accountCount;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              {isEmailOnly ? 'Request Email Hosting' : 'Request Domain Registration'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{domain}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="modal-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Domain info banner */}
        {!isEmailOnly && (
          <div className="mx-6 mt-4 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="font-mono text-sm font-semibold text-blue-800">{domain}</span>
              <span className="text-xs text-blue-500">·</span>
              <span className="text-xs text-blue-600">{years} Year{years > 1 ? 's' : ''}</span>
            </div>
            <span className="text-sm font-bold text-blue-700">{fmt(price)}</span>
          </div>
        )}

        {/* "Purchasing for client" banner — shown when SP/CP buys for a registered client */}
        {forClientId && forClientName && (
          <div className="mx-6 mt-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2" data-testid="for-client-banner">
            <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <div className="text-xs">
              <span className="font-semibold text-amber-800">Purchasing on behalf of:</span>{' '}
              <span className="text-amber-700 font-medium">{forClientName}</span>
            </div>
          </div>
        )}

        <div className="px-6 py-4 space-y-5">

          {/* Email Hosting — always shown, always mandatory */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-800">Email Hosting</span>
              <span className="text-[10px] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Included</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Professional email accounts are included with every domain registration.
            </p>

            {/* Pricing badge */}
            <div className="flex items-center gap-2 mb-3 p-2.5 bg-green-50 border border-green-100 rounded-xl">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-slate-400 line-through">
                  {fmt(settings.email_original_price)}/account/year
                </span>
                <span className="text-sm font-bold text-green-700">
                  {fmt(settings.email_price_per_account)}/account/year
                </span>
                <span className="text-[10px] font-semibold bg-green-600 text-white px-1.5 py-0.5 rounded-full">
                  SAVE {Math.round((1 - settings.email_price_per_account / settings.email_original_price) * 100)}%
                </span>
              </div>
            </div>

            {/* Feature bullets */}
            {settings.email_hosting_features?.length > 0 && (
              <ul className="mb-3 grid grid-cols-1 gap-1">
                {settings.email_hosting_features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            )}

            {/* Number of accounts */}
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">
                Number of Email Accounts
              </label>
              <div className="flex flex-wrap gap-2">
                {ACCOUNT_COUNTS.map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setAccountCount(n)}
                    data-testid={`account-count-${n}`}
                    className={`w-12 h-10 rounded-xl text-sm font-bold border-2 transition-all
                      ${accountCount === n
                        ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-105'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Per year: <span className="font-semibold text-slate-600">{fmt(emailCostPerYear)}/year</span>
                {years > 1 && <>&nbsp;·&nbsp;Total ({years} yrs): <span className="font-semibold text-slate-600">{fmt(emailCostTotal)}</span></>}
              </p>
            </div>
          </div>

          {/* Email usernames — required, count must match accountCount */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Email Usernames
              </label>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                allFilled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {filledCount}/{accountCount} filled
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-3">
              Enter a username for each email account. All {accountCount} are required.
            </p>
            <div className="space-y-2">
              {usernames.map((u, idx) => (
                <div key={u.id}>
                  <div className={`flex items-center border rounded-xl overflow-hidden transition-colors ${
                    usernameErrors.includes(u.id)
                      ? 'border-red-300 ring-2 ring-red-100'
                      : 'border-slate-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:border-blue-300'
                  }`}>
                    <span className="text-xs text-slate-400 bg-slate-50 border-r border-slate-200 px-2.5 py-2 whitespace-nowrap font-mono">
                      {idx + 1}.
                    </span>
                    <input
                      type="text"
                      placeholder={['info', 'support', 'sales', 'admin', 'hello', 'billing', 'team'][idx] || 'username'}
                      value={u.value}
                      onChange={e => updateUsername(u.id, e.target.value)}
                      className="flex-1 px-3 py-2 text-sm focus:outline-none bg-white"
                      data-testid={`email-username-${idx}`}
                    />
                    <span className="px-2.5 text-xs text-slate-400 bg-slate-50 border-l border-slate-200 whitespace-nowrap py-2 font-mono">
                      @{domain}
                    </span>
                  </div>
                  {usernameErrors.includes(u.id) && (
                    <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Required
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1.5">
              Notes <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <textarea
              rows={2}
              placeholder="Any special instructions or requirements..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
              data-testid="domain-request-notes"
            />
          </div>

          {/* Domain Security (only for new domain requests) */}
          {!isEmailOnly && (
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Domain Security</span>
              </div>

              <div className="space-y-2.5">
                {/* Domain Protector+ */}
                <label
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors select-none ${domainProtector ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/60'}`}
                  data-testid="domain-protector-label"
                >
                  <input
                    type="checkbox"
                    checked={domainProtector}
                    onChange={e => setDomainProtector(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                    data-testid="domain-protector-checkbox"
                  />
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Lock className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-sm font-medium text-slate-700">Domain Protector+</span>
                      <span className="ml-1 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-1.5 py-0.5">
                        +{fmt(settings.domain_protector_price)}/year
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">Locks domain to prevent unauthorized transfers &amp; hijacking</p>
                  </div>
                </label>

                {/* WHOIS Privacy */}
                <label
                  className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:border-slate-300 hover:bg-slate-50/60 transition-colors select-none"
                  data-testid="whois-privacy-label"
                >
                  <input
                    type="checkbox"
                    checked={whoisPrivacy}
                    onChange={e => setWhoisPrivacy(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                    data-testid="whois-privacy-checkbox"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">WHOIS Privacy</span>
                      <span className="ml-1 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-1.5 py-0.5">
                        +{fmt(settings.whois_privacy_price)}/year
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">Hides your personal info from public WHOIS database</p>
                  </div>
                </label>

                {/* SSL Certificate */}
                <label
                  className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:border-slate-300 hover:bg-slate-50/60 transition-colors select-none"
                  data-testid="ssl-certificate-label"
                >
                  <input
                    type="checkbox"
                    checked={sslRequired}
                    onChange={e => setSslRequired(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                    data-testid="ssl-certificate-checkbox"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">SSL Certificate</span>
                      <span className="text-xs font-semibold text-green-600 bg-green-50 border border-green-100 rounded-full px-1.5 py-0.5">Free</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">Enables HTTPS — auto-provisioned via Let's Encrypt</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* ── Order Total Summary ── */}
          <div className="border-t border-slate-200 pt-4" data-testid="order-summary">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3">Order Summary</p>
            <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
              <div className="divide-y divide-slate-100">
                {!isEmailOnly && (
                  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-slate-600">Domain Registration ({years} yr{years > 1 ? 's' : ''})</span>
                    <span className="font-semibold text-slate-800">{fmt(price)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-slate-600">
                    Email Hosting ({accountCount} accounts × {years} yr{years > 1 ? 's' : ''})
                    <span className="ml-1 text-xs text-slate-400 line-through">{fmt(emailOriginalTotal)}</span>
                  </span>
                  <span className="font-semibold text-slate-800">{fmt(emailCostTotal)}</span>
                </div>
                {whoisPrivacy && !isEmailOnly && (
                  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-slate-600">WHOIS Privacy ({years} yr{years > 1 ? 's' : ''})</span>
                    <span className="font-semibold text-slate-800">{fmt(whoisCost)}</span>
                  </div>
                )}
                {domainProtector && !isEmailOnly && (
                  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-slate-600">Domain Protector+ ({years} yr{years > 1 ? 's' : ''})</span>
                    <span className="font-semibold text-slate-800">{fmt(domainProtectorCost)}</span>
                  </div>
                )}
                {sslRequired && !isEmailOnly && (
                  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-slate-500">SSL Certificate</span>
                    <span className="font-semibold text-green-600">Free</span>
                  </div>
                )}
                {/* GST separator line */}
                <div className="flex items-center justify-between px-4 py-2.5 text-sm bg-slate-100/60">
                  <span className="text-slate-500">Subtotal (excl. GST)</span>
                  <span className="font-medium text-slate-700">{fmt(subtotalPreGST)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 text-sm bg-slate-100/60">
                  <span className="text-slate-500">GST ({GST_RATE}% IGST)</span>
                  <span className="font-medium text-slate-700">+{fmt(gstAmount)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-blue-600">
                <span className="text-sm font-bold text-blue-100">Total Due (incl. GST)</span>
                <span className="text-base font-extrabold text-white" data-testid="total-due-amount">{fmt(totalDue)}</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              * Prices inclusive of 18% GST. Email hosting billed for {years} year{years > 1 ? 's' : ''} matching domain registration period.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pb-5 border-t border-slate-100 pt-4">
          <div className="text-xs text-slate-400">
            {!allFilled && (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertCircle className="w-3.5 h-3.5" />
                Fill in all {accountCount} usernames to continue
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={submitting} className="text-sm">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm gap-2"
              data-testid="submit-domain-request-btn"
            >
              {submitting && (
                <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              )}
              {isEmailOnly ? 'Submit Email Request' : 'Submit Request'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
